import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { CheckoutSteps } from "@/components/billing/CheckoutSteps";
import { db, type PackageRecord } from "@/lib/billing";
import type { TemplateRecord } from "@/lib/packageAccess";
import { useAuth } from "@/hooks/useAuth";

type InfoForm = {
  project_name: string;
  package_id: string;
  template_id: string;
  language: string;
  category: string;
  domain_preference: string;
  subdomain_preference: string;
  brand_name: string;
  brand_colors: string;
  logo_preferences: string;
  content_tone: string;
  target_audience: string;
  publishing_frequency: string;
  publishing_channels: string;
};

const initialForm: InfoForm = {
  project_name: "",
  package_id: "",
  template_id: "",
  language: "fr",
  category: "actualites",
  domain_preference: "",
  subdomain_preference: "",
  brand_name: "",
  brand_colors: "",
  logo_preferences: "",
  content_tone: "Professionnel, clair, editorial",
  target_audience: "",
  publishing_frequency: "quotidienne",
  publishing_channels: "Site géré, site Delapresse",
};

const fallbackTemplates: TemplateRecord[] = [
  {
    id: "fallback-le-quotidien-local",
    slug: "le-quotidien-local",
    name: "Le Quotidien Local",
    description: "Template francais pour quotidien regional avec rubriques locales, opinion et direct.",
    template_type: "news",
    category: "Actualites locales",
    target_niche: "Villes et regions",
    language: "fr",
    preview_image_url: null,
    demo_url: null,
    features: ["Une editoriale", "Rubriques locales", "Bloc opinion", "Newsletter"],
    default_sections: ["A la une", "Politique locale", "Economie", "Culture", "Opinion"],
    required_package_slug: "news-site-starter",
    required_service_slug: "news-site",
    is_active: true,
    is_exclusive: false,
    claimed_by_website_id: null,
    claimed_at: null,
    exclusivity_edition: null,
    layout_key: null,
    theme: {},
    created_at: "",
    updated_at: "",
  },
  {
    id: "fallback-media-pro-live",
    slug: "media-pro-live",
    name: "Media Pro Live",
    description: "Experience premium pour medias actifs avec direct, video, SEO et newsletters.",
    template_type: "broadcast",
    category: "Media professionnel",
    target_niche: "Actualite nationale",
    language: "fr",
    preview_image_url: null,
    demo_url: null,
    features: ["Live updates", "SEO avance", "Video embed", "Push newsletter"],
    default_sections: ["Breaking", "Direct", "Analyse", "Videos", "Newsletter"],
    required_package_slug: "news-site-pro",
    required_service_slug: "automation-auto-publishing",
    is_active: true,
    is_exclusive: false,
    claimed_by_website_id: null,
    claimed_at: null,
    exclusivity_edition: null,
    layout_key: null,
    theme: {},
    created_at: "",
    updated_at: "",
  },
  {
    id: "fallback-reseau-magazine",
    slug: "reseau-magazine",
    name: "Reseau Magazine",
    description: "Template magazine pour reseaux multi-sites avec dossiers, medias et editions thematiques.",
    template_type: "magazine",
    category: "Magazine",
    target_niche: "Culture, lifestyle et societe",
    language: "fr",
    preview_image_url: null,
    demo_url: null,
    features: ["Dossiers", "Media library", "Series editoriales", "Traduction"],
    default_sections: ["Dossier du jour", "Portraits", "Guides", "Galeries", "International"],
    required_package_slug: "media-network",
    required_service_slug: "media-library",
    is_active: true,
    is_exclusive: false,
    claimed_by_website_id: null,
    claimed_at: null,
    exclusivity_edition: null,
    layout_key: null,
    theme: {},
    created_at: "",
    updated_at: "",
  },
  {
    id: "fallback-business-brief-en",
    slug: "business-brief-en",
    name: "Business Brief EN",
    description: "English business news template with market briefs, analysis and executive summary blocks.",
    template_type: "digital",
    category: "Business",
    target_niche: "Finance and economy",
    language: "en",
    preview_image_url: null,
    demo_url: null,
    features: ["Market briefs", "Executive summary", "Charts", "API embeds"],
    default_sections: ["Top stories", "Markets", "Companies", "Opinion", "Data"],
    required_package_slug: "media-network",
    required_service_slug: "api-embed-access",
    is_active: true,
    is_exclusive: false,
    claimed_by_website_id: null,
    claimed_at: null,
    exclusivity_edition: null,
    layout_key: null,
    theme: {},
    created_at: "",
    updated_at: "",
  },
  {
    id: "fallback-starter-demo-fr",
    slug: "starter-demo-fr",
    name: "Starter Demo FR",
    description: "Modele de demonstration limite pour essais sans publication live.",
    template_type: "demo",
    category: "Essai",
    target_niche: "Validation de concept",
    language: "fr",
    preview_image_url: null,
    demo_url: null,
    features: ["Brouillon", "Preview", "Sections simples"],
    default_sections: ["Accueil", "Articles recents", "A propos"],
    required_package_slug: "trial",
    required_service_slug: "news-site",
    is_active: true,
    is_exclusive: false,
    claimed_by_website_id: null,
    claimed_at: null,
    exclusivity_edition: null,
    layout_key: null,
    theme: {},
    created_at: "",
    updated_at: "",
  },
];

function isUuid(value: string) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function isMissingTableError(error: unknown, tableName: string) {
  const message = error instanceof Error ? error.message : String((error as any)?.message ?? error ?? "");
  return message.includes(`'public.${tableName}'`) || message.includes(`"${tableName}"`) || message.includes(`table '${tableName}'`);
}

async function fetchOptions() {
  const [packagesResult, templatesResult] = await Promise.all([
    db.from("packages").select("*").eq("is_active", true).order("sort_order"),
    db.from("site_templates").select("*").eq("is_active", true).order("sort_order"),
  ]);

  let packages = (packagesResult.data ?? []) as PackageRecord[];
  let packagesWarning: string | null = null;
  let templates = (templatesResult.data ?? []) as TemplateRecord[];
  let templatesWarning: string | null = null;

  if (packagesResult.error || packages.length === 0) {
    const fallback = await db
      .from("subscription_plans")
      .select("id, name, slug, monthly_price, is_active, article_limit, disk_space_mb, ai_credits, features")
      .eq("is_active", true)
      .order("monthly_price");

    if (!fallback.error && fallback.data?.length) {
      packages = fallback.data.map((plan: any, index: number) => ({
        id: plan.id,
        name: plan.name,
        slug: plan.slug,
        description: null,
        audience: null,
        monthly_price: Number(plan.monthly_price ?? 0),
        currency: "eur",
        is_trial: false,
        is_enterprise: false,
        is_active: Boolean(plan.is_active),
        sort_order: index + 1,
        trial_days: null,
        stripe_product_id: null,
        stripe_price_id: null,
        max_websites: null,
        max_articles_per_month: plan.article_limit ?? null,
        max_draft_articles: null,
        max_ai_credits: plan.ai_credits ?? null,
        max_media_assets: null,
        max_team_members: null,
        storage_mb: plan.disk_space_mb ?? null,
        bandwidth_mb: null,
        publishing_methods: [],
        support_level: "basic",
        features: Array.isArray(plan.features) ? { items: plan.features } : plan.features ?? {},
      })) as PackageRecord[];
      packagesWarning = "legacy_packages";
    } else if (packagesResult.error) {
      throw packagesResult.error;
    }
  }

  if (templatesResult.error) {
    console.warn("site_templates is unavailable for project info options", templatesResult.error);
    templates = fallbackTemplates;
    templatesWarning = null;
  }

  return { packages, templates, packagesWarning, templatesWarning };
}

export default function InfoPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ["project-info-options"], queryFn: fetchOptions });
  const [form, setForm] = useState<InfoForm>(() => ({ ...initialForm, package_id: params.get("package") ?? "", template_id: params.get("template") ?? "" }));

  useEffect(() => {
    const saved = JSON.parse(sessionStorage.getItem("delapresse_checkout") || "{}");
    setForm((current) => ({
      ...current,
      package_id: current.package_id || saved.packageId || params.get("package") || "",
      template_id: current.template_id || saved.templateId || params.get("template") || "",
    }));
  }, [params]);

  const saveInfo = useMutation({
    mutationFn: async (status: "draft" | "ready_for_checkout") => {
      if (!user) throw new Error(t("checkout.info.notSignedIn"));
      const selectedTemplate = data?.templates.find((template) => template.id === form.template_id);
      const payload = {
        user_id: user.id,
        package_id: form.package_id || null,
        template_id: form.template_id && isUuid(form.template_id) ? form.template_id : null,
        status,
        project_name: form.project_name || form.brand_name || "Projet Delapresse",
        language: form.language,
        category: form.category,
        domain_preference: form.domain_preference || null,
        subdomain_preference: form.subdomain_preference || null,
        branding: {
          brand_name: form.brand_name,
          colors: form.brand_colors,
          logo_preferences: form.logo_preferences,
        },
        content_preferences: {
          tone: form.content_tone,
          target_audience: form.target_audience,
        },
        publishing_preferences: {
          frequency: form.publishing_frequency,
          channels: form.publishing_channels,
          selected_template: form.template_id && !isUuid(form.template_id) ? form.template_id : null,
        },
      };
      sessionStorage.setItem("delapresse_checkout", JSON.stringify({
        packageId: form.package_id,
        templateId: form.template_id,
        intake: {
          ...payload,
          selected_template_name: selectedTemplate?.name ?? null,
          stored_locally_at: new Date().toISOString(),
        },
      }));

      const { error } = await db.from("project_intakes").insert(payload);
      if (error) {
        if (isMissingTableError(error, "project_intakes")) {
          console.warn("project_intakes is unavailable; continuing with session checkout data", error);
          return { storedLocally: true };
        }
        throw error;
      }
      return { storedLocally: false };
    },
    onSuccess: (result, status) => {
      queryClient.invalidateQueries({ queryKey: ["project-info-options"] });
      toast.success(result?.storedLocally
        ? t("checkout.info.savedLocally")
        : status === "draft" ? t("checkout.info.draftSaved") : t("checkout.info.confirmed"));
      if (status === "ready_for_checkout") navigate(`/checkout/review?package=${form.package_id}`);
    },
    onError: (err: any) => toast.error(err?.message ?? t("checkout.info.saveError")),
  });

  const set = (patch: Partial<InfoForm>) => setForm((current) => ({ ...current, ...patch }));

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b border-border bg-background/80 px-6 py-4 backdrop-blur-xl">
        <h1 className="text-lg font-semibold">{t("checkout.info.title")}</h1>
        <p className="text-xs text-muted-foreground">{t("checkout.info.subtitle")}</p>
      </header>
      <main className="mx-auto max-w-6xl space-y-6 p-6">
        <CheckoutSteps current={1} />
        {isLoading ? (
          <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
            <div className="space-y-6">
              <Card>
                <CardHeader><CardTitle className="text-base">{t("checkout.info.packageTemplate")}</CardTitle></CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-2">
                  <Field label="Package">
                    <Select value={form.package_id || "none"} onValueChange={(value) => set({ package_id: value === "none" ? "" : value })} disabled={!data?.packages.length}>
                      <SelectTrigger><SelectValue placeholder={t("checkout.info.selectPackage")} /></SelectTrigger>
                      <SelectContent><SelectItem value="none">{t("checkout.info.selectPackage")}</SelectItem>{data?.packages.map((pkg) => <SelectItem key={pkg.id} value={pkg.id}>{pkg.name}</SelectItem>)}</SelectContent>
                    </Select>
                    {data?.packagesWarning && <p className="text-xs leading-5 text-warning">{t(`checkout.info.warnings.${data.packagesWarning}`)}</p>}
                    {!data?.packages.length && (
                      <p className="text-xs leading-5 text-destructive">
                        {error ? t("checkout.info.packageLoadError", { message: (error as Error).message }) : t("checkout.info.noActivePackages")}
                      </p>
                    )}
                  </Field>
                  <Field label="Template">
                    <Select value={form.template_id || "none"} onValueChange={(value) => set({ template_id: value === "none" ? "" : value })} disabled={!data?.templates.length}>
                      <SelectTrigger><SelectValue placeholder={t("checkout.info.selectTemplate")} /></SelectTrigger>
                      <SelectContent><SelectItem value="none">{t("checkout.info.selectTemplate")}</SelectItem>{data?.templates.map((template) => <SelectItem key={template.id} value={template.id}>{template.name}</SelectItem>)}</SelectContent>
                    </Select>
                    {data?.templatesWarning && <p className="text-xs leading-5 text-warning">{data.templatesWarning}</p>}
                  </Field>
                  <Field label={t("checkout.info.mainLanguage")}>
                    <Select value={form.language} onValueChange={(language) => set({ language })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="fr">{t("settings.french")}</SelectItem><SelectItem value="en">{t("settings.english")}</SelectItem><SelectItem value="ar">{t("checkout.info.arabic")}</SelectItem></SelectContent>
                    </Select>
                  </Field>
                  <Field label={t("checkout.info.category")}><Input value={form.category} onChange={(e) => set({ category: e.target.value })} /></Field>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-base">{t("checkout.info.siteDomain")}</CardTitle></CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-2">
                  <Field label={t("checkout.info.projectName")}><Input value={form.project_name} onChange={(e) => set({ project_name: e.target.value })} placeholder={t("checkout.info.projectNamePlaceholder")} /></Field>
                  <Field label={t("checkout.info.brandName")}><Input value={form.brand_name} onChange={(e) => set({ brand_name: e.target.value })} placeholder={t("checkout.info.brandNamePlaceholder")} /></Field>
                  <Field label={t("checkout.info.domainWanted")}><Input value={form.domain_preference} onChange={(e) => set({ domain_preference: e.target.value })} placeholder="actualites-lyon.fr" /></Field>
                  <Field label={t("checkout.info.subdomainWanted")}><Input value={form.subdomain_preference} onChange={(e) => set({ subdomain_preference: e.target.value })} placeholder="lyon.delapresse.fr" /></Field>
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-base">{t("checkout.info.brandingContent")}</CardTitle></CardHeader>
                <CardContent className="grid gap-4 md:grid-cols-2">
                  <Field label={t("checkout.info.brandColors")}><Textarea value={form.brand_colors} onChange={(e) => set({ brand_colors: e.target.value })} /></Field>
                  <Field label={t("checkout.info.logoPreferences")}><Textarea value={form.logo_preferences} onChange={(e) => set({ logo_preferences: e.target.value })} /></Field>
                  <Field label={t("checkout.info.contentTone")}><Textarea value={form.content_tone} onChange={(e) => set({ content_tone: e.target.value })} /></Field>
                  <Field label={t("checkout.info.targetAudience")}><Textarea value={form.target_audience} onChange={(e) => set({ target_audience: e.target.value })} /></Field>
                  <Field label={t("checkout.info.publishingFrequency")}><Input value={form.publishing_frequency} onChange={(e) => set({ publishing_frequency: e.target.value })} /></Field>
                  <Field label={t("checkout.info.publishingChannels")}><Input value={form.publishing_channels} onChange={(e) => set({ publishing_channels: e.target.value })} /></Field>
                </CardContent>
              </Card>
            </div>
            <Card className="h-fit">
              <CardHeader><CardTitle className="text-base">{t("common.actions")}</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                <Button variant="outline" className="w-full" onClick={() => saveInfo.mutate("draft")} disabled={saveInfo.isPending}>
                  <Save className="mr-2 h-4 w-4" /> {t("checkout.actions.saveDraft")}
                </Button>
                <Button className="w-full" onClick={() => saveInfo.mutate("ready_for_checkout")} disabled={saveInfo.isPending || !form.package_id}>
                  {t("checkout.actions.continueCheckout")} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <p className="text-xs leading-5 text-muted-foreground">
                  {t("checkout.info.actionsHint")}
                </p>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="space-y-2"><Label>{label}</Label>{children}</div>;
}
